const DBA_TRADING_NAME = /\bdba\s+(.+)$/i;
const SHORT_LEGAL_SUFFIX_BY_LONG_FORM: ReadonlyMap<string, string> = new Map([
    ["incorporated", "inc"],
    ["corporation", "corp"],
    ["limited", "ltd"],
]);
const LEGAL_SUFFIXES: ReadonlySet<string> = new Set([
    ...SHORT_LEGAL_SUFFIX_BY_LONG_FORM.keys(),
    "pllc",
    "llc",
    "inc",
    "corp",
    "ltd",
]);
// Shorter names are too generic to match loosely, e.g. "AB LLC" vs "AB".
const MIN_LOOSE_MATCH_LENGTH = 4;

interface CompanyNamesMatchParams {
    companyName: string;
    entityName: string;
}

interface TradingNameLocationParams {
    companyName: string;
    tradingName: string;
}

export function nameWords(name: string): string[] {
    return name
        .toLowerCase()
        .split(/[^\p{L}\p{N}]+/u)
        .filter((word) => word.length > 0);
}

export function normalizeCompanyName(name: string): string {
    return nameWords(name).join("");
}

export function companyNamesMatch({
    companyName,
    entityName,
}: CompanyNamesMatchParams): boolean {
    const company = normalizeCompanyName(companyName);
    if (!company) {
        return false;
    }
    if (company === normalizeCompanyName(entityName)) {
        return true;
    }
    if (company.length < MIN_LOOSE_MATCH_LENGTH) {
        return false;
    }
    if (namesEquivalent(companyName, entityName)) {
        return true;
    }

    const tradingName = DBA_TRADING_NAME.exec(entityName)?.[1]?.trim();
    if (!tradingName) {
        return false;
    }
    if (namesEquivalent(companyName, tradingName)) {
        return true;
    }
    return companyNameIsTradingNameLocation({ companyName, tradingName });
}

function companyNameIsTradingNameLocation({
    companyName,
    tradingName,
}: TradingNameLocationParams): boolean {
    const foldedCompany = companyName.toLowerCase().trim();
    const foldedTrading = tradingName.toLowerCase();
    if (
        normalizeCompanyName(tradingName).length < MIN_LOOSE_MATCH_LENGTH ||
        !foldedCompany.startsWith(foldedTrading)
    ) {
        return false;
    }
    return foldedCompany
        .slice(foldedTrading.length)
        .trimStart()
        .startsWith("-");
}

function namesEquivalent(companyName: string, candidateName: string): boolean {
    if (normalizeCompanyName(candidateName).length < MIN_LOOSE_MATCH_LENGTH) {
        return false;
    }
    const companyWords = withShortLegalSuffix(nameWords(companyName));
    const candidateWords = withShortLegalSuffix(nameWords(candidateName));
    const company = companyWords.join("");
    const candidate = candidateWords.join("");
    return (
        company === candidate ||
        withoutLegalSuffix(companyWords).join("") === candidate ||
        withoutLegalSuffix(candidateWords).join("") === company
    );
}

function hasRoomForLegalSuffix(words: string[]): boolean {
    return words.slice(0, -1).join("").length >= MIN_LOOSE_MATCH_LENGTH;
}

function withShortLegalSuffix(words: string[]): string[] {
    const shortForm = SHORT_LEGAL_SUFFIX_BY_LONG_FORM.get(words.at(-1) ?? "");
    return shortForm && hasRoomForLegalSuffix(words)
        ? [...words.slice(0, -1), shortForm]
        : words;
}

function withoutLegalSuffix(words: string[]): string[] {
    return LEGAL_SUFFIXES.has(words.at(-1) ?? "") &&
        hasRoomForLegalSuffix(words)
        ? words.slice(0, -1)
        : words;
}

const COMPANY_NAME_WORDS: ReadonlySet<string> = new Set([
    ...LEGAL_SUFFIXES,
    "dba",
    "co",
    "company",
    "lp",
    "llp",
    "plc",
    "gmbh",
    "group",
    "holdings",
    "partners",
    "associates",
    "enterprises",
    "industries",
    "services",
    "solutions",
    "systems",
    "technologies",
    "consulting",
    "international",
]);

export function isLikelyCompanyName(name: string): boolean {
    return (
        /[\d&@]/.test(name) ||
        nameWords(name).some((word) => COMPANY_NAME_WORDS.has(word))
    );
}
